import { type FormEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { Balloons, type BalloonsHandle } from "@/components/ui/balloons";
import { GradientDots } from "@/components/ui/gradient-dots";
import { HyperText } from "@/components/ui/hyper-text";
import { consentRequestSchema, emailSchema, type ConsentApiError } from "../shared/consentSchema";
import { submitConsent } from "./api";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  consents: {
    currentRecruitment: boolean;
    futureRecruitment: boolean;
    shareWithClients: boolean;
    informationClause: boolean;
    privacyPolicy: boolean;
    withdrawalAcknowledged: boolean;
  };
  companyWebsite: string;
};

type ConsentKey = keyof FormState["consents"];

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; submissionId: string; requestId: string }
  | { kind: "error"; message: string; requestId?: string };

const initialState: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  consents: {
    currentRecruitment: false,
    futureRecruitment: false,
    shareWithClients: false,
    informationClause: false,
    privacyPolicy: false,
    withdrawalAcknowledged: false
  },
  companyWebsite: ""
};

const fieldLabels: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  email: "Email address",
  "consents.currentRecruitment": "Current recruitment consent",
  "consents.futureRecruitment": "Future recruitment consent",
  "consents.shareWithClients": "Client sharing consent",
  "consents.informationClause": "Information clause",
  "consents.privacyPolicy": "Privacy policy",
  "consents.withdrawalAcknowledged": "Withdrawal acknowledgement"
};

const consentKeys: ConsentKey[] = [
  "currentRecruitment",
  "futureRecruitment",
  "shareWithClients",
  "withdrawalAcknowledged",
  "informationClause",
  "privacyPolicy"
];

function getFieldId(path: string): string {
  return path.replaceAll(".", "-");
}

function getEmailError(value: string): string | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = emailSchema.safeParse(value);
  return parsed.success ? undefined : parsed.error.issues[0]?.message;
}

export function App() {
  const [form, setForm] = useState<FormState>(initialState);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });
  const balloonsRef = useRef<BalloonsHandle>(null);

  const selectedConsentCount = useMemo(
    () => consentKeys.filter((key) => form.consents[key]).length,
    [form.consents]
  );
  const allConsentsSelected = selectedConsentCount === consentKeys.length;

  const updateField = (field: "firstName" | "lastName" | "email", value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
    setFieldErrors((current) => {
      const next = { ...current };
      const fieldError = field === "email" ? getEmailError(value) : undefined;

      if (fieldError) {
        next[field] = fieldError;
      } else {
        delete next[field];
      }

      return next;
    });
  };

  const updateConsent = (field: ConsentKey, value: boolean) => {
    setForm((current) => ({
      ...current,
      consents: {
        ...current.consents,
        [field]: value
      }
    }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[`consents.${field}`];
      return next;
    });
  };

  const updateAllConsents = (value: boolean) => {
    setForm((current) => ({
      ...current,
      consents: consentKeys.reduce<FormState["consents"]>(
        (next, key) => ({
          ...next,
          [key]: value
        }),
        current.consents
      )
    }));

    if (value) {
      setFieldErrors((current) => {
        const next = { ...current };
        for (const key of consentKeys) {
          delete next[`consents.${key}`];
        }
        return next;
      });
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitState({ kind: "submitting" });

    const parsed = consentRequestSchema.safeParse(form);

    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join(".");
        if (path && !fields[path]) {
          fields[path] = issue.message;
        }
      }
      setFieldErrors(fields);
      setSubmitState({ kind: "idle" });
      return;
    }

    try {
      const result = await submitConsent(parsed.data);
      balloonsRef.current?.launchAnimation();
      setSubmitState({
        kind: "success",
        submissionId: result.submissionId,
        requestId: result.requestId
      });
      setFieldErrors({});
      setForm(initialState);
    } catch (error) {
      const details = (error as Error & { details?: ConsentApiError }).details;
      setFieldErrors(details?.fields ?? {});
      setSubmitState({
        kind: "error",
        message: details?.message ?? "The form could not be submitted. Please try again later.",
        requestId: details?.requestId
      });
    }
  };

  if (submitState.kind === "success") {
    return (
      <main className="app-shell">
        <BackgroundLayer />
        <section className="success-panel" aria-labelledby="success-title">
          <img className="brand-logo" src="/assets/logo-white.png" alt="Bytamic" />
          <p className="section-kicker">Consent received</p>
          <h1 id="success-title">Thank you. Your consent has been recorded.</h1>
          <p>
            We have saved your consent for the recruitment process. You can withdraw your consent at
            any time using the contact channel provided in the information clause.
          </p>
          <dl className="reference-list" aria-label="Submission reference">
            <div>
              <dt>Submission ID</dt>
              <dd>{submitState.submissionId}</dd>
            </div>
            <div>
              <dt>Request ID</dt>
              <dd>{submitState.requestId}</dd>
            </div>
          </dl>
          <button className="secondary-button" type="button" onClick={() => setSubmitState({ kind: "idle" })}>
            Submit another consent
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <BackgroundLayer />
      <Balloons ref={balloonsRef} className="success-balloons" />
      <section className="intro-panel" aria-labelledby="page-title">
        <div className="brand-row">
          <img className="brand-logo" src="/assets/logo-white.png" alt="Bytamic" />
        </div>
        <p className="section-kicker">Bytamic recruitment</p>
        <h1 id="page-title">Personal data consent</h1>
        <p className="intro-copy">
          Review the statements and confirm how Bytamic may process your data for this recruitment
          process.
        </p>
        <div className="consent-summary" aria-label="What happens after you confirm">
          <h2>What happens after you confirm?</h2>
          <ul className="summary-list">
            <li>
              <strong>Current process</strong>
              <span>Bytamic can process the personal data included in your application.</span>
            </li>
            <li>
              <strong>Future recruitment</strong>
              <span>Your profile can remain available for upcoming Bytamic recruitment processes.</span>
            </li>
            <li>
              <strong>Client roles</strong>
              <span>Your CV data can be forwarded to Bytamic clients when connected with an application.</span>
            </li>
            <li>
              <strong>Withdrawal</strong>
              <span>You can withdraw your consent at any time using the channel provided by Bytamic.</span>
            </li>
          </ul>
          <div className="summary-link">
            <span>Need more information?</span>
            <a href="https://www.bytamic.com/privacy-policy/" target="_blank" rel="noreferrer">
              View privacy policy
            </a>
          </div>
        </div>
      </section>

      <form className="consent-form" onSubmit={handleSubmit} noValidate>
        <FormSection
          marker="01"
          title="Your details"
          description="Use the details connected with your application or contract communication."
        >
          <div className="field-grid">
            <TextField
              id="firstName"
              label="First name"
              value={form.firstName}
              error={fieldErrors.firstName}
              autoComplete="given-name"
              onChange={(value) => updateField("firstName", value)}
            />
            <TextField
              id="lastName"
              label="Last name"
              value={form.lastName}
              error={fieldErrors.lastName}
              autoComplete="family-name"
              onChange={(value) => updateField("lastName", value)}
            />
          </div>
          <TextField
            id="email"
            label="Email address"
            value={form.email}
            error={fieldErrors.email}
            autoComplete="email"
            inputMode="email"
            type="email"
            onChange={(value) => updateField("email", value)}
          />
        </FormSection>

        <FormSection
          marker="02"
          title="Consent statements"
          description="Select all statements to send this recruitment consent to Bytamic."
          aside={`${selectedConsentCount} of ${consentKeys.length} selected`}
        >
          <div className="select-all-row">
            <input
              id="select-all-consents"
              type="checkbox"
              checked={allConsentsSelected}
              aria-describedby="select-all-consents-note"
              onChange={(event) => updateAllConsents(event.target.checked)}
            />
            <div>
              <label htmlFor="select-all-consents">Select all</label>
              <p id="select-all-consents-note">
                This marks every consent statement required for Bytamic's recruitment process.
              </p>
            </div>
          </div>

          <ConsentCheckbox
            id="consents.currentRecruitment"
            checked={form.consents.currentRecruitment}
            required
            error={fieldErrors["consents.currentRecruitment"]}
            label="I consent to Bytamic processing the personal data included in my CV for the current recruitment process."
            detail="This allows Bytamic to review and manage your application."
            onChange={(value) => updateConsent("currentRecruitment", value)}
          />

          <ConsentCheckbox
            id="consents.futureRecruitment"
            checked={form.consents.futureRecruitment}
            required
            error={fieldErrors["consents.futureRecruitment"]}
            label="I consent to Bytamic processing the personal data included in my CV for future recruitment processes."
            detail="This allows Bytamic to keep your candidate profile available for later opportunities."
            onChange={(value) => updateConsent("futureRecruitment", value)}
          />

          <ConsentCheckbox
            id="consents.shareWithClients"
            checked={form.consents.shareWithClients}
            required
            error={fieldErrors["consents.shareWithClients"]}
            label="I consent to Bytamic forwarding personal data contained in my CV to Bytamic clients in connection with an employment application."
            detail="This applies when a recruitment process concerns a role connected with a Bytamic client."
            onChange={(value) => updateConsent("shareWithClients", value)}
          />

          <ConsentCheckbox
            id="consents.withdrawalAcknowledged"
            checked={form.consents.withdrawalAcknowledged}
            required
            error={fieldErrors["consents.withdrawalAcknowledged"]}
            label="I am aware that I can withdraw my consent at any time by informing Bytamic."
            detail="Withdrawal does not affect processing carried out before the withdrawal."
            onChange={(value) => updateConsent("withdrawalAcknowledged", value)}
          />

          <ConsentCheckbox
            id="consents.informationClause"
            checked={form.consents.informationClause}
            required
            error={fieldErrors["consents.informationClause"]}
            label="I declare that I have read the information clause."
            detail="The information clause explains how your personal data is stored, used and shared."
            linkText="Open information clause"
            linkHref="https://www.bytamic.com/bytamic-information-clause/"
            onChange={(value) => updateConsent("informationClause", value)}
          />

          <ConsentCheckbox
            id="consents.privacyPolicy"
            checked={form.consents.privacyPolicy}
            required
            error={fieldErrors["consents.privacyPolicy"]}
            label="I confirm that detailed information about personal data processing is available in Bytamic's privacy policy."
            detail="Open the policy before submitting if you need more information."
            linkText="Open privacy policy"
            linkHref="https://www.bytamic.com/privacy-policy/"
            onChange={(value) => updateConsent("privacyPolicy", value)}
          />
        </FormSection>

        <div className="visually-hidden" aria-hidden="true">
          <label htmlFor="companyWebsite">Company website</label>
          <input
            id="companyWebsite"
            name="companyWebsite"
            tabIndex={-1}
            autoComplete="off"
            value={form.companyWebsite}
            onChange={(event) => setForm((current) => ({ ...current, companyWebsite: event.target.value }))}
          />
        </div>

        {submitState.kind === "error" ? (
          <div className="form-alert" role="alert">
            <strong>{submitState.message}</strong>
            {submitState.requestId ? <span>Reference: {submitState.requestId}</span> : null}
          </div>
        ) : null}

        <div className="legal-note" id="information-clause">
          <h2>Before you submit</h2>
          <p>
            Detailed information about personal data processing is available in Bytamic's public
            legal documents.
          </p>
          <div className="legal-links" id="privacy-policy">
            <a href="https://www.bytamic.com/bytamic-information-clause/" target="_blank" rel="noreferrer">
              Information clause
            </a>
            <a href="https://www.bytamic.com/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy policy
            </a>
            <a href="https://www.bytamic.com/data-protection-policy/" target="_blank" rel="noreferrer">
              Data protection policy
            </a>
            <a href="https://www.bytamic.com/cookies-policy/" target="_blank" rel="noreferrer">
              Cookies policy
            </a>
          </div>
        </div>

        <div className="submit-row">
          <button className="primary-button" type="submit" disabled={submitState.kind === "submitting"}>
            {submitState.kind === "submitting" ? "Submitting..." : "Submit consent"}
          </button>
          <p>All consent statements must be selected before the consent can be recorded.</p>
        </div>
      </form>
    </main>
  );
}

function BackgroundLayer() {
  return (
    <div className="gradient-dots-layer" aria-hidden="true">
      <GradientDots />
    </div>
  );
}

type FormSectionProps = {
  marker: string;
  title: string;
  description: string;
  aside?: string;
  children: ReactNode;
};

function FormSection({ marker, title, description, aside, children }: FormSectionProps) {
  const titleId = `form-section-${marker}`;

  return (
    <section className="form-section" aria-labelledby={titleId}>
      <div className="form-section-header">
        <span className="form-section-marker" aria-hidden="true">
          {marker}
        </span>
        <div>
          <h2 id={titleId}>
            <HyperText className="form-section-title" text={title} duration={680} />
          </h2>
          <p>{description}</p>
        </div>
        {aside ? <span className="form-section-aside">{aside}</span> : null}
      </div>
      <div className="form-section-body">{children}</div>
    </section>
  );
}

type TextFieldProps = {
  id: "firstName" | "lastName" | "email";
  label: string;
  value: string;
  error?: string;
  autoComplete: string;
  inputMode?: "email";
  type?: "email" | "text";
  onChange: (value: string) => void;
};

function TextField({ id, label, value, error, autoComplete, inputMode, type = "text", onChange }: TextFieldProps) {
  const errorId = `${id}-error`;

  return (
    <div className={`field-block${error ? " has-field-error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={id}
        type={type}
        className={error ? "input-error" : undefined}
        value={value}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <p className="field-error" id={errorId} aria-live="polite">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type ConsentCheckboxProps = {
  id: keyof typeof fieldLabels;
  checked: boolean;
  required?: boolean;
  error?: string;
  label: string;
  detail: string;
  linkText?: string;
  linkHref?: string;
  onChange: (value: boolean) => void;
};

function ConsentCheckbox({
  id,
  checked,
  required,
  error,
  label,
  detail,
  linkText,
  linkHref,
  onChange
}: ConsentCheckboxProps) {
  const inputId = getFieldId(id);
  const errorId = `${inputId}-error`;

  return (
    <div className={`consent-option${error ? " has-error" : ""}`}>
      <input
        id={inputId}
        name={id}
        type="checkbox"
        checked={checked}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div>
        <label htmlFor={inputId}>
          {label} {required ? <span className="required-label">Required</span> : null}
        </label>
        <p>{detail}</p>
        {linkText && linkHref ? (
          <a href={linkHref} target={linkHref.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
            {linkText}
          </a>
        ) : null}
        {error ? (
          <p className="field-error" id={errorId}>
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
